'use client'

import AddCommentField from '@/app/[locale]/[roomId]/components/AddCommentField'
import Comment from '@/app/[locale]/[roomId]/components/Comment'
import SortPopoverButton from '@/app/[locale]/[roomId]/components/SortPopoverButton'
import { LoadingDots } from '@/components/ui/LoadingDots'
import { ReportReason } from '@/constants/comment'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useWorldDetailsContext } from '@/context/WorldDetailsContext'
import {
  deleteCommentAction,
  postCommentAction,
  reportCommentAction,
  toggleLikeAction,
} from '@/server/lib/comment'
import { getComments, getCommentsCount } from '@/services/cms'
import {
  Comment as CommentType,
  CommentsData,
  CommentsSortOptions,
  SortOptionValue,
} from '@/types/comments'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
import { useIntersectionObserver } from 'usehooks-ts'

const COMMENT_PAGE_LIMIT = 50

export const COMMENTS_SORT_OPTIONS: CommentsSortOptions = {
  top: { label: 'top-comments', value: 'top' },
  latest: { label: 'latest', value: 'latest' },
}

function sortComments(comments: CommentType[], value: SortOptionValue) {
  const copied = [...comments]
  if (value === 'top') {
    copied.sort((a, b) => b.like_count - a.like_count)
  } else if (value === 'latest') {
    copied.sort((a, b) => Date.parse(b.date_created) - Date.parse(a.date_created))
  }
  return copied
}

function CommentsPanel() {
  const t = useTranslations()
  const { comments: initialComments = [], commentsCount: initialCommentsCount = 0 } =
    useWorldDetailsContext()
  const { roomData } = useCheckEnterRoomContext()
  const { store } = useConfigsContext()

  const [sortOrder, setSortOrder] = useState<SortOptionValue>(COMMENTS_SORT_OPTIONS['top'].value)
  const [isSorting, setIsSorting] = useState(false)
  const [commentsCount, setCommentsCount] = useState(initialCommentsCount)
  const [tempComments, setTempComments] = useState<CommentType[]>([])
  const [isAddingComment, setIsAddingComment] = useState(false)
  const {
    ref: lastCommentRef,
    isIntersecting,
    entry,
  } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '100px',
  })

  const { data: commentsCountData, mutate: mutateCommentsCount } = useSWR(
    'comments/count',
    () => getCommentsCount({ roomId: roomData.id }),
    { revalidateOnFocus: false },
  )

  const getKey = (pageIndex: number, previousPageData: CommentsData | null) => {
    if (!roomData?.id) return null
    if (pageIndex === 0 || !previousPageData)
      return `comments?limit=${COMMENT_PAGE_LIMIT}&room_id=${roomData.id}&cursor=`

    if (!previousPageData || !previousPageData.pages?.next) return null

    return previousPageData.pages.next
  }

  const { data, isLoading, isValidating, setSize, mutate } = useSWRInfinite(
    getKey,
    (key) => {
      const urlParams = new URLSearchParams(key.split('?')[1])
      const cursor = urlParams.get('cursor') || ''
      return getComments({
        roomId: roomData.id,
        cursor: cursor,
        limit: COMMENT_PAGE_LIMIT,
        htcAuthKey: store?.credentials?.htcToken,
      })
    },
    { revalidateFirstPage: false },
  )

  const baseComments: CommentType[] = useMemo(() => {
    return data && data?.length > 0 ? data.flatMap((page) => page?.results || []) : initialComments
  }, [data, initialComments])

  useEffect(() => {
    if (baseComments.length === 0) return

    const baseIds = new Set(baseComments.map((comment) => comment.id))

    setTempComments((prev) => {
      const hasOverlap = prev.some((comment) => baseIds.has(comment.id))
      if (hasOverlap) {
        return prev.filter((comment) => !baseIds.has(comment.id))
      }
      return prev
    })
  }, [baseComments])

  const renderComments: CommentType[] = useMemo(() => {
    const tempIds = new Set(tempComments.map((comment) => comment.id))
    const filteredBase = baseComments.filter((comment) => !tempIds.has(comment.id))
    return [...tempComments, ...sortComments([...filteredBase], sortOrder)]
  }, [tempComments, baseComments, sortOrder])

  const canLoadMore = !!(
    !isLoading &&
    !isValidating &&
    data &&
    data.length > 0 &&
    data[data.length - 1]?.pages?.next
  )

  const addComment = async (value: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!value.trim() || !htcToken || !roomData?.id) return

    try {
      setIsAddingComment(true)
      const created = await postCommentAction({
        roomId: roomData.id,
        content: value,
        htcAuthKey: htcToken,
      })
      setTempComments((prev) => [created, ...prev])
      setCommentsCount((prev) => prev + 1)
    } catch (err) {
      console.error('Failed to add comment:', err)
    }
    setIsAddingComment(false)
  }

  const updateTempReplyCount = (commentId: string, delta: number) => {
    setTempComments((prev) =>
      prev.map((comment) =>
        comment.id === commentId
          ? { ...comment, reply_count: Math.max(0, (comment.reply_count || 0) + delta) }
          : comment,
      ),
    )
    setCommentsCount((prev) => Math.max(0, prev + delta))
  }

  const updateReplyMutate = (commentId: string, delta: number) => {
    mutate(
      (pages) =>
        pages?.map((page) => ({
          ...page,
          results: (page?.results || []).map((comment: CommentType) =>
            comment.id === commentId
              ? {
                  ...comment,
                  reply_count: Math.max(0, (comment.reply_count || 0) + delta),
                }
              : comment,
          ),
        })),
      false,
    )
    setCommentsCount((prev) => Math.max(0, prev + delta))
  }

  const updateReplyCount = (commentId: string, delta: number) => {
    const existedInTemp = tempComments.some((comment) => comment.id === commentId)
    if (existedInTemp) {
      updateTempReplyCount(commentId, delta)
    } else {
      updateReplyMutate(commentId, delta)
    }
  }

  const updateLikeCount = (commentId: string, isLiked: boolean) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    toggleLikeAction({ commentId, htcAuthKey: htcToken, isLiked })
  }

  const reportCommentMutate = async (commentId: string, reason: ReportReason, detail: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = data
    try {
      mutate(
        (pages) =>
          pages?.map((page) => ({
            ...page,
            results: page?.results?.map((comment) =>
              comment.id === commentId ? { ...comment, is_reported: true } : comment,
            ),
          })),
        false,
      )
      await reportCommentAction({ commentId, htcAuthKey: htcToken, reason, detail })
    } catch (err) {
      mutate(cache, false)
      console.error('Failed to report comment:', err)
    }
  }

  const removeTempComment = async (commentId: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = tempComments
    let replyCount = 0

    try {
      setTempComments((prev) =>
        prev.filter((comment) => {
          if (comment.id === commentId) {
            replyCount = comment.reply_count || 0
          }
          return comment.id !== commentId
        }),
      )
      setCommentsCount((prev) => Math.max(0, prev - 1 - replyCount))
      await deleteCommentAction({ commentId, htcAuthKey: htcToken })
    } catch (err) {
      setTempComments(cache)
      setCommentsCount((prev) => prev + 1 + replyCount)
      console.error('Failed to delete temp comment:', err)
    }
  }

  const removeCommentMutate = async (commentId: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = data
    let replyCount = 0

    try {
      mutate(
        (pages) =>
          pages?.map((page) => ({
            ...page,
            results: (page?.results || []).filter((comment: CommentType) => {
              if (comment.id === commentId) {
                replyCount = comment.reply_count || 0
              }
              return comment.id !== commentId
            }),
          })),
        false,
      )
      setCommentsCount((prev) => Math.max(0, prev - 1 - replyCount))
      await deleteCommentAction({ commentId, htcAuthKey: htcToken })
    } catch (err) {
      mutate(cache, false)
      setCommentsCount((prev) => prev + 1 + replyCount)
      console.error('Failed to delete comment:', err)
    }
  }

  const removeComment = (commentId: string) => {
    const existedInTemp = tempComments.some((comment) => comment.id === commentId)
    if (existedInTemp) {
      removeTempComment(commentId)
    } else {
      removeCommentMutate(commentId)
    }
  }

  const handleSort = async (sortOrder: SortOptionValue) => {
    setIsSorting(true)
    await mutate()
    await mutateCommentsCount()
    setSortOrder(sortOrder)
    setIsSorting(false)
  }

  useEffect(() => {
    if (isIntersecting && canLoadMore) {
      setSize((prev) => prev + 1)
    }
  }, [isIntersecting, canLoadMore, entry, setSize])

  useEffect(() => {
    if (commentsCountData) {
      setCommentsCount(commentsCountData.pages?.total ?? 0)
    }
  }, [commentsCountData])

  return (
    <div className="3xl:max-w-[1440px] mb-12 flex h-fit w-full flex-col justify-between gap-5 md:mb-20 md:gap-6 lg:col-start-1 lg:row-start-2 lg:max-w-[75vw]">
      <div className="flex items-center justify-between">
        <span className="header2-omni-styles">{`${commentsCount || ''} ${t('comments-panel.comments-count')}`}</span>
        <SortPopoverButton sortOrder={sortOrder} setSortOrder={handleSort} />
      </div>
      <AddCommentField addComment={addComment} />
      <hr className="bg-vive-surface-normal border-[0.5px]" />
      <div className="flex h-fit w-full flex-col gap-5">
        {isAddingComment && <LoadingDots size="md" />}
        {!isSorting &&
          renderComments.map((comment, index) => {
            const isLastComment = index === renderComments.length - 1

            return (
              <Comment
                key={comment.id}
                comment={comment}
                lastCommentRef={isLastComment && canLoadMore ? lastCommentRef : undefined}
                removeComment={removeComment}
                updateCommentReplyCount={updateReplyCount}
                updateCommentLikeCount={updateLikeCount}
                reportComment={reportCommentMutate}
              />
            )
          })}
        {isLoading || (isValidating && <LoadingDots size="md" />)}
      </div>
    </div>
  )
}

export default CommentsPanel
